import { motion } from "framer-motion";
import { Gauge, Flame, Waves, MapPin, Layers, ShieldAlert, Radio, Sun, Moon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useSim } from "../lib/store";
import {
  energyJoules, tntKilotons, estimatePGA, faultType, soilMultiplier,
  tsunamiVelocityKmh, magnitudeToParticleCount, eruptionJoules, energyWithDuration, SOIL_TYPES,
} from "../lib/seismic";

function Metric({ icon: Icon, label, value, sub, testid, accent = "text-red-500" }) {
  return (
    <div data-testid={testid} className="rounded-xl bg-black/40 border border-white/10 hover:border-red-500/30 transition-colors px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] font-mono-tele uppercase tracking-[0.15em] text-zinc-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`mt-1 font-mono-tele text-base font-bold tabular-nums ${accent}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export function TelemetryHUD() {
  const { magnitude, duration, depth, soilType, epicenter, simulating, tsunamiEnabled, activeEruption } = useSim();
  const events = useSim((s) => s.events);
  const jQuake = energyWithDuration(magnitude, duration);
  const jVolcanic = activeEruption ? eruptionJoules(magnitude) : 0;
  const j = jQuake + jVolcanic;
  const tnt = tntKilotons(j);
  const pga = estimatePGA(magnitude, soilMultiplier(soilType));
  const count = magnitudeToParticleCount(magnitude, 16000);
  const vel = tsunamiVelocityKmh(depth * 100 + 3000);
  const vuln = Math.min(100, Math.round((magnitude - 4) * 14 + soilMultiplier(soilType) * 12 + (30 - Math.min(depth, 30)) * 0.6));

  return (
    <>
      {/* LEFT: energy + geology */}
      <motion.aside
        initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
        className={`fixed left-4 z-30 hidden lg:flex flex-col gap-2.5 w-60 transition-[top] duration-300 ${events.length ? "top-[336px]" : "top-32"}`}
      >
        <h3 className="text-[10px] font-mono-tele uppercase tracking-[0.25em] leading-loose text-zinc-500 px-1 pt-0.5">Total Energy Release</h3>
        <Metric testid="telemetry-joules-display" icon={Flame} label="Total Energy" value={`${j.toExponential(2)} J`} sub={activeEruption ? `${tnt.toLocaleString(undefined, { maximumFractionDigits: 0 })} kt · incl. volcanic` : `${tnt.toLocaleString(undefined, { maximumFractionDigits: 0 })} kt TNT eq.`} accent="text-orange-400" />
        <Metric testid="telemetry-pga-display" icon={Gauge} label="Peak Ground Accel." value={`${pga.toFixed(1)} %g`} sub={`Soil: ${SOIL_TYPES.find((s) => s.id === soilType)?.label}`} />
        <Metric testid="telemetry-fault-display" icon={Layers} label="Focal Mechanism" value={faultType(magnitude)} sub={`Depth ${depth.toFixed(0)} km`} accent="text-amber-400" />
        <Metric testid="telemetry-tsunami-display" icon={Waves} label="Tsunami Velocity" value={epicenter?.offshore && tsunamiEnabled ? `${vel.toFixed(0)} km/h` : "—"} sub={epicenter?.offshore ? "Open-ocean propagation" : "Onshore — no wavefront"} accent="text-cyan-400" />
      </motion.aside>

      {/* RIGHT: source + wavefront */}
      <motion.aside
        initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.35 }}
        className="fixed top-24 right-4 z-30 hidden lg:flex flex-col gap-2.5 w-64"
      >
        <h3 className="text-[10px] font-mono-tele uppercase tracking-[0.25em] text-zinc-500 px-1 text-right">Source Telemetry</h3>
        <Metric testid="telemetry-coords-display" icon={MapPin} label="Epicenter" value={epicenter ? `${epicenter.lat.toFixed(2)}, ${epicenter.lon.toFixed(2)}` : "—"} sub={epicenter ? (epicenter.offshore ? "Oceanic crust" : "Continental crust") : "Awaiting pin drop"} />
        <Metric testid="telemetry-particles-display" icon={Radio} label="Sand-Energy Particles" value={simulating ? count.toLocaleString() : "0"} sub={simulating ? "Active GPU instances" : "Idle"} accent="text-red-500" />
        <Metric testid="telemetry-wavefront-display" icon={Radio} label="Wavefront Vector" value={epicenter?.offshore && tsunamiEnabled ? "Rayleigh + Tsunami" : "P → S → Rayleigh"} accent="text-zinc-200" />
        <Metric testid="telemetry-vulnerability-display" icon={ShieldAlert} label="Vulnerability Index" value={`${vuln}/100`} sub={vuln > 70 ? "Critical exposure" : vuln > 45 ? "Elevated risk" : "Moderate"} accent={vuln > 70 ? "text-red-500" : "text-amber-400"} />
      </motion.aside>
    </>
  );
}

export function StreetHUD() {
  const { magnitude, duration, soilType, setSoilType, simulating, simProgress, timeOfDay, setDayPreset, activeEruption } = useSim();
  const isDay = timeOfDay >= 6 && timeOfDay <= 18.5;
  const integrity = Math.max(0, Math.round(100 - (magnitude - 4) * 12 * soilMultiplier(soilType) * (simulating ? 0.5 + simProgress : 0.5)));
  const resonance = (0.5 + (magnitude - 4) * 0.35).toFixed(2);
  const j = energyWithDuration(magnitude, duration) + (activeEruption ? eruptionJoules(magnitude) : 0);
  const particles = simulating ? magnitudeToParticleCount(magnitude, 16000) : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="fixed top-24 left-4 z-30 w-[88vw] max-w-sm p-4 rounded-2xl bg-zinc-950/75 backdrop-blur-xl border border-white/15 shadow-[0_16px_48px_rgba(0,0,0,0.8)]"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-200 flex items-center gap-2">
          <Layers className="h-4 w-4 text-red-500" /> Structural Fracture Sandbox
        </h3>
        <button
          data-testid="day-night-toggle"
          onClick={() => setDayPreset(isDay ? "dark" : "sunny")}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all ${
            isDay ? "bg-amber-500/20 border-amber-400/50 text-amber-200" : "bg-indigo-950/50 border-indigo-400/40 text-indigo-200"
          }`}
        >
          {isDay ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          {isDay ? "Day" : "Night"}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-xl bg-black/40 border border-white/10 px-3 py-2">
          <div className="text-[10px] font-mono-tele uppercase tracking-wider text-zinc-500">Resonance Freq.</div>
          <div className="font-mono-tele text-lg font-bold text-orange-400">{resonance} Hz</div>
        </div>
        <div className="rounded-xl bg-black/40 border border-white/10 px-3 py-2">
          <div className="text-[10px] font-mono-tele uppercase tracking-wider text-zinc-500">Structural Integrity</div>
          <div className={`font-mono-tele text-lg font-bold ${integrity < 40 ? "text-red-500" : integrity < 70 ? "text-amber-400" : "text-emerald-400"}`}>{integrity}%</div>
        </div>
      </div>
      <div className="h-2 rounded-full bg-zinc-800 overflow-hidden mb-3">
        <div className={`h-full transition-all ${integrity < 40 ? "bg-red-600" : integrity < 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${integrity}%` }} />
      </div>
      {/* live telemetry (also available in street view) */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div data-testid="telemetry-joules-display" className="rounded-xl bg-black/40 border border-white/10 px-3 py-2">
          <div className="text-[10px] font-mono-tele uppercase tracking-wider text-zinc-400">Total Energy</div>
          <div className="font-mono-tele text-base font-bold text-orange-400">{j.toExponential(2)} J</div>
        </div>
        <div data-testid="telemetry-particles-display" className="rounded-xl bg-black/40 border border-white/10 px-3 py-2">
          <div className="text-[10px] font-mono-tele uppercase tracking-wider text-zinc-400">Sand Particles</div>
          <div className="font-mono-tele text-base font-bold text-red-500">{particles.toLocaleString()}</div>
        </div>
      </div>
      <label className="text-[10px] font-mono-tele uppercase tracking-wider text-zinc-500">Soil Type (liquefaction multiplier)</label>
      <Select value={soilType} onValueChange={setSoilType}>
        <SelectTrigger data-testid="soil-type-selector" className="mt-1 w-full bg-black/50 border-white/15 text-sm text-zinc-200 focus:border-red-500/50 focus:ring-red-500/30">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-white/15 text-zinc-200">
          {SOIL_TYPES.map((s) => (
            <SelectItem key={s.id} value={s.id} className="text-zinc-200 focus:bg-red-600/20 focus:text-white">
              {s.label} (×{s.multiplier})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </motion.div>
  );
}
